import React, { useState, useEffect } from 'react';
import TabNavigation from '../../components/ui/TabNavigation';
import QuickActionBar from '../../components/ui/QuickActionBar';
import Icon from '../../components/AppIcon';

import WeeklyMenuGrid from './components/WeeklyMenuGrid';
import GroceryListPanel from './components/GroceryListPanel';
import MealLibrary from './components/MealLibrary';
import { Helmet } from 'react-helmet';

const WeeklyMenuPlanning = () => {
  const [activeTab, setActiveTab] = useState('menu'); // 'menu' or 'grocery'
  const [familyMembers, setFamilyMembers] = useState([]);
  const [weeklyMenu, setWeeklyMenu] = useState({});
  const [groceryItems, setGroceryItems] = useState([]);
  const [showMealLibrary, setShowMealLibrary] = useState(false);

  // Load family members from localStorage
  useEffect(() => {
    const savedMembers = localStorage?.getItem('familyMembers');
    if (savedMembers) {
      setFamilyMembers(JSON.parse(savedMembers));
    } else {
      const defaultMembers = [
        { id: 1, name: 'Sarah', role: 'Mom', color: '#EF4444', initials: 'SM' },
        { id: 2, name: 'Michael', role: 'Dad', color: '#3B82F6', initials: 'MD' },
        { id: 3, name: 'Emma', role: 'Daughter', color: '#EC4899', initials: 'ED' },
        { id: 4, name: 'Lucas', role: 'Son', color: '#10B981', initials: 'LS' }
      ];
      setFamilyMembers(defaultMembers);
    }
  }, []);

  // Load weekly menu from localStorage
  useEffect(() => {
    const savedMenu = localStorage?.getItem('weeklyMenu');
    if (savedMenu) {
      setWeeklyMenu(JSON.parse(savedMenu));
    } else {
      // Initialize empty menu structure
      const emptyMenu = {};
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const meals = ['breakfast', 'lunch', 'dinner'];
      
      days?.forEach(day => {
        emptyMenu[day] = {};
        meals?.forEach(meal => {
          emptyMenu[day][meal] = null;
        });
      });
      
      setWeeklyMenu(emptyMenu);
    }
  }, []);

  // Load grocery items from localStorage (migrated from messaging interface)
  useEffect(() => {
    const savedConversations = localStorage?.getItem('familyConversations');
    if (savedConversations) {
      const conversations = JSON.parse(savedConversations);
      const groceryConversation = conversations?.find(c => c?.isGroceryList);
      if (groceryConversation?.groceryItems) {
        setGroceryItems(groceryConversation?.groceryItems);
      }
    }
  }, []);

  // Save weekly menu to localStorage
  useEffect(() => {
    if (Object.keys(weeklyMenu)?.length > 0) {
      localStorage?.setItem('weeklyMenu', JSON.stringify(weeklyMenu));
    }
  }, [weeklyMenu]);

  // Save grocery items to localStorage
  useEffect(() => {
    if (groceryItems?.length >= 0) {
      localStorage?.setItem('weeklyMenuGroceryItems', JSON.stringify(groceryItems));
      
      // Also update the messaging interface grocery list for backward compatibility
      const savedConversations = localStorage?.getItem('familyConversations');
      if (savedConversations) {
        const conversations = JSON.parse(savedConversations);
        const updatedConversations = conversations?.map(conv => {
          if (conv?.isGroceryList) {
            const checkedCount = groceryItems?.filter(item => item?.checked)?.length || 0;
            const totalCount = groceryItems?.length || 0;
            return {
              ...conv,
              groceryItems: groceryItems,
              lastMessage: `${checkedCount}/${totalCount} items completed`,
              lastMessageTime: new Date()?.toISOString()
            };
          }
          return conv;
        });
        localStorage?.setItem('familyConversations', JSON.stringify(updatedConversations));
      }
    }
  }, [groceryItems]);

  const handleUpdateGroceryItems = (updatedItems) => {
    setGroceryItems(updatedItems);
  };

  const handleAddMeal = (day, mealType, mealData) => {
    setWeeklyMenu(prev => ({
      ...prev,
      [day]: {
        ...prev?.[day],
        [mealType]: mealData
      }
    }));
  };

  const handleRemoveMeal = (day, mealType) => {
    setWeeklyMenu(prev => ({
      ...prev,
      [day]: {
        ...prev?.[day],
        [mealType]: null
      }
    }));
  };

  const handleQuickAction = (action) => {
    if (action?.id === 'add-meal') {
      setShowMealLibrary(true);
    } else if (action?.id === 'add-grocery-item') {
      setActiveTab('grocery');
    } else if (action?.id === 'generate-grocery-list') {
      // Auto-generate grocery list from weekly menu
      const newItems = [];
      Object.entries(weeklyMenu)?.forEach(([day, meals]) => {
        Object.entries(meals)?.forEach(([mealType, meal]) => {
          if (meal?.ingredients) {
            meal?.ingredients?.forEach(ingredient => {
              if (!groceryItems?.some(item => item?.text?.toLowerCase() === ingredient?.toLowerCase())) {
                newItems?.push({
                  id: Date.now() + Math.random(),
                  text: ingredient,
                  checked: false,
                  addedBy: 1,
                  addedAt: new Date()?.toISOString()
                });
              }
            });
          }
        });
      });
      if (newItems?.length > 0) {
        setGroceryItems(prev => [...prev, ...newItems]);
        setActiveTab('grocery');
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Menu Planning - Starling Schedule</title>
        <meta name="description" content="Plan your family's weekly meals and manage grocery shopping lists" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <TabNavigation />
        
        <div className="container mx-auto px-4 py-6">
          <div className="mb-6">
            <h1 className="text-h1 font-heading text-foreground mb-2">
              Weekly Menu Planning
            </h1>
            <p className="text-muted-foreground">
              Plan meals for the week and manage your grocery shopping list
            </p>
          </div>

          {/* Tab Selector */}
          <div className="mb-6 flex gap-2 border-b border-border">
            <button
              onClick={() => setActiveTab('menu')}
              className={`px-6 py-3 font-medium transition-colors relative ${
                activeTab === 'menu' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="calendar-days" size={20} />
                <span>Weekly Menu</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('grocery')}
              className={`px-6 py-3 font-medium transition-colors relative ${
                activeTab === 'grocery' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="shopping-cart" size={20} />
                <span>Grocery List</span>
                {groceryItems?.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                    {groceryItems?.filter(item => !item?.checked)?.length}
                  </span>
                )}
              </div>
            </button>
          </div>

          {/* Content Area */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {activeTab === 'menu' ? (
              <WeeklyMenuGrid
                weeklyMenu={weeklyMenu}
                familyMembers={familyMembers}
                onAddMeal={handleAddMeal}
                onRemoveMeal={handleRemoveMeal}
                onShowMealLibrary={() => setShowMealLibrary(true)}
              />
            ) : (
              <GroceryListPanel
                items={groceryItems}
                onUpdateItems={handleUpdateGroceryItems}
                familyMembers={familyMembers}
              />
            )}
          </div>
        </div>

        {/* Meal Library Modal */}
        {showMealLibrary && (
          <MealLibrary
            onClose={() => setShowMealLibrary(false)}
            onSelectMeal={(meal) => {
              // Handle meal selection
              setShowMealLibrary(false);
            }}
          />
        )}

        <QuickActionBar
          context="menu"
          onActionSelect={handleQuickAction}
        />
      </div>
    </>
  );
};

export default WeeklyMenuPlanning;